import { GameState } from "@/models";
import type { AgeBucket, GamesFilterSearch, WeightBucket } from "./GamesFilters";
import { DEFAULT_SORT, SORT_KEYS, type SortKey, type SortRule } from "./GamesSortMenu";

const WEIGHT_BUCKETS: WeightBucket[] = ["light", "medium", "medium-heavy", "heavy"];
const AGE_BUCKETS: AgeBucket[] = ["0-6", "7-9", "10-12", "13plus"];
const GAME_STATES: string[] = Object.values(GameState);

/** Every rule off, which the URL has to be able to say as well as "unset". */
const NO_SORT = "none";

/**
 * How many cards to a page.
 *
 * A hundred by default: enough that a normal collection is one page, few
 * enough that a library filled from the rankings does not render a thousand
 * covers at once.
 */
export const PAGE_SIZES = [25, 50, 75, 100] as const;
export const DEFAULT_PAGE_SIZE = 100;

/**
 * "Show me everything", as opposed to not having said.
 *
 * The two used to be the same absent param, which was fine while absent meant
 * everything. Now that a real collection defaults the filter to Owned, asking
 * for everything has to be something the URL can say, or picking Any would
 * clear the param and snap straight back to Owned.
 */
export const ANY_STATUS = "any";

const parsePositiveInt = (value: unknown): number | undefined => {
	const parsed = Number(value);
	return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const parseString = (value: unknown): string | undefined =>
	typeof value === "string" && value.length > 0 ? value : undefined;

/** A search param holding one value or many; the URL cannot tell them apart. */
const toStringList = (value: unknown): string[] | undefined => {
	if (typeof value === "string" && value.length > 0) return [value];
	if (Array.isArray(value)) {
		const items = value.filter((x): x is string => typeof x === "string" && x.length > 0);
		return items.length > 0 ? items : undefined;
	}
	return undefined;
};

/**
 * The rules that are on, in the order they are applied. Rules that are off are
 * left out entirely, so the common case is a short param or none at all.
 */
const encodeRules = (rules: SortRule[]): string =>
	SORT_KEYS.map((key) => rules.find((rule) => rule.key === key))
		.filter((rule): rule is SortRule => rule?.enabled === true)
		.map((rule) => `${rule.key}:${rule.descending ? "desc" : "asc"}`)
		.join(",") || NO_SORT;

const DEFAULT_ENCODED = encodeRules(DEFAULT_SORT);

export const encodeSort = (rules: SortRule[]): string | undefined => {
	const encoded = encodeRules(rules);
	return encoded === DEFAULT_ENCODED ? undefined : encoded;
};

export const decodeSort = (value: unknown): SortRule[] => {
	if (typeof value !== "string" || value.length === 0) {
		return DEFAULT_SORT;
	}

	const directions = new Map<SortKey, boolean>();
	for (const token of value.split(",")) {
		const [key, direction] = token.split(":");
		if ((SORT_KEYS as string[]).includes(key)) {
			directions.set(key as SortKey, direction === "desc");
		}
	}

	// Precedence is always the order the menu lists them in - there is no way to
	// reorder the rules, so the param only says which are on and which way.
	return DEFAULT_SORT.map((rule) => {
		const descending = directions.get(rule.key);
		return descending === undefined ? { ...rule, enabled: false } : { ...rule, enabled: true, descending };
	});
};

/**
 * Both the card list and the table read the same params, so a link from one to
 * the other only has to carry the search across untouched.
 */
export const validateGamesSearch = (search: Record<string, unknown>): GamesFilterSearch => {
	const weight = search.weight as WeightBucket;
	const age = search.age as AgeBucket;
	const status = parseString(search.status);
	return {
		name: parseString(search.name),
		category: parseString(search.category),
		type: parseString(search.type),
		// One value arrives as a string, several as an array - the URL does
		// not distinguish, so both are normalised to a list.
		mechanics: toStringList(search.mechanics),
		players: parsePositiveInt(search.players),
		playTime: parsePositiveInt(search.playTime),
		weight: WEIGHT_BUCKETS.includes(weight) ? weight : undefined,
		age: AGE_BUCKETS.includes(age) ? age : undefined,
		status:
			status === ANY_STATUS
				? ANY_STATUS
				: status !== undefined && GAME_STATES.includes(status)
					? (status as GameState)
					: undefined,
		language: parseString(search.language),
		sort: parseString(search.sort),
		page: parsePositiveInt(search.page),
		// Anything else would be a page size nothing offers, and a hand-edited
		// URL should not be able to ask for one game at a time.
		size: (PAGE_SIZES as readonly number[]).includes(Number(search.size)) ? Number(search.size) : undefined,
	};
};

/** Applies a change and drops what it cleared, so the URL only holds live filters. */
export const mergeSearch = (previous: GamesFilterSearch, partial: Partial<GamesFilterSearch>): GamesFilterSearch => {
	const next = { ...previous, ...partial };
	for (const key of Object.keys(next) as (keyof GamesFilterSearch)[]) {
		if (next[key] === undefined) delete next[key];
	}
	return next;
};
